import React, { useRef, useState } from 'react';
import {
  Box, Paper, Typography, IconButton, LinearProgress, Stack, Tooltip,
  Alert, Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  CloudUpload as UploadIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
  CheckCircle as DoneIcon,
  ErrorOutline as ErrorIcon,
  Block as CancelledIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  LinkOff as DisconnectedIcon,
  FolderOpen as ReselectIcon,
} from '@mui/icons-material';
import { useUploadManager } from '../contexts/UploadManagerContext';

function fmtBytes(b) {
  if (!b || b < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// Network throughput is conventionally reported in bits/sec.
function fmtBitsPerSec(bytesPerSec) {
  if (!bytesPerSec || bytesPerSec < 0) return '0 bps';
  const bits = bytesPerSec * 8;
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  let i = 0; let v = bits;
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
function fmtEta(s) {
  if (s == null || !isFinite(s) || s < 0) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function UploadManagerOverlay() {
  const { tasks, cancel, pause, resume, dismiss, clearFinished, reselectAndResume } = useUploadManager();
  const [minimized, setMinimized] = useState(false);
  const [reselectError, setReselectError] = useState({}); // id -> error string
  // One hidden file input per source_unavailable task, keyed by id, so each
  // re-select dialog only shows the file the user is supposed to re-pick.
  const reselectInputsRef = useRef(new Map());

  if (tasks.length === 0) return null;

  // source_unavailable is recoverable (re-select brings it back), so it
  // counts as "active" for the header pill. paused too. Done/error/
  // cancelled are the only truly-finished states.
  const active = tasks.filter((t) =>
    t.status === 'uploading'
    || t.status === 'queued'
    || t.status === 'paused'
    || t.status === 'source_unavailable',
  );
  const finished = tasks.length - active.length;
  const totalSize = tasks.reduce((s, t) => s + (t.size || 0), 0);
  const totalLoaded = tasks.reduce((s, t) => s + (t.loaded || 0), 0);
  const overallPct = totalSize > 0 ? Math.round((totalLoaded / totalSize) * 100) : 0;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: minimized ? 260 : 380,
        maxHeight: minimized ? 56 : '60vh',
        // Below MUI's modal layer (1300) so a Dialog opened over the
        // upload manager always receives clicks. Previously this was
        // 1400, sitting above modals — every click on a Dialog whose
        // bounds overlapped the upload overlay was eaten.
        zIndex: 1100,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        onClick={minimized ? () => setMinimized(false) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          cursor: minimized ? 'pointer' : 'default',
        }}
      >
        <UploadIcon fontSize="small" />
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {active.length > 0
            ? `Uploading ${active.length} file${active.length === 1 ? '' : 's'} · ${overallPct}%`
            : `${finished} upload${finished === 1 ? '' : 's'} finished`}
        </Typography>
        {finished > 0 && active.length === 0 && (
          <Tooltip title="Clear">
            <IconButton size="small" sx={{ color: 'inherit' }} onClick={(e) => { e.stopPropagation(); clearFinished(); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={minimized ? 'Expand' : 'Minimize'}>
          <IconButton
            size="small"
            sx={{ color: 'inherit' }}
            onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}
          >
            {minimized ? <ExpandLessIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      {!minimized && (
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {tasks.map((t) => {
              const pct = t.size > 0 ? Math.round((t.loaded / t.size) * 100) : 0;
              return (
                <Box key={t.id} sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {t.status === 'done' && <DoneIcon fontSize="small" color="success" />}
                    {t.status === 'error' && <ErrorIcon fontSize="small" color="error" />}
                    {t.status === 'cancelled' && <CancelledIcon fontSize="small" color="disabled" />}
                    {t.status === 'source_unavailable' && <DisconnectedIcon fontSize="small" color="warning" />}
                    <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }} noWrap title={t.fileName}>
                      {t.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {pct}%
                    </Typography>
                    {(t.status === 'uploading' || t.status === 'paused') ? (
                      <>
                        {t.status === 'uploading' ? (
                          <Tooltip title="Pause">
                            <IconButton size="small" onClick={() => pause(t.id)}>
                              <PauseIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Resume">
                            <IconButton size="small" color="primary" onClick={() => resume(t.id)}>
                              <ResumeIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={() => cancel(t.id)}>
                            <CancelIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : t.status === 'source_unavailable' ? (
                      <Tooltip title="Cancel (server-side bytes will be discarded)">
                        <IconButton size="small" onClick={() => cancel(t.id)}>
                          <CancelIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={() => dismiss(t.id)}>
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={t.status === 'done' ? 100 : pct}
                    sx={{ height: 6, borderRadius: 1 }}
                    color={
                      t.status === 'error' ? 'error'
                      : t.status === 'done' ? 'success'
                      : t.status === 'cancelled' ? 'inherit'
                      : t.status === 'paused' ? 'warning'
                      : t.status === 'source_unavailable' ? 'warning'
                      : 'primary'
                    }
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtBytes(t.loaded)} / {fmtBytes(t.size)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t.status === 'uploading'
                        ? `${fmtBitsPerSec(t.speed)} · ETA ${fmtEta(t.etaSeconds)}`
                        : t.status === 'paused' ? 'Paused'
                        : t.status === 'source_unavailable' ? 'Source disconnected'
                        : t.status === 'done' ? 'Complete'
                        : t.status === 'cancelled' ? 'Cancelled'
                        : t.error || 'Failed'}
                    </Typography>
                  </Box>

                  {t.status === 'source_unavailable' && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem', lineHeight: 1.4 } }}>
                        <strong>Lost access to the source file.</strong>{' '}
                        The drive or share holding it disconnected. Re-mount it,
                        then click <strong>Re-select file to resume</strong>.
                        {Math.round((t.loaded / Math.max(1, t.size)) * 100)}% is already on the server — pick the same file
                        and we'll continue from where we left off.
                      </Alert>
                      {reselectError[t.id] && (
                        <Alert severity="error" sx={{ mt: 0.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem', lineHeight: 1.4 } }} onClose={() => setReselectError((m) => ({ ...m, [t.id]: null }))}>
                          {reselectError[t.id]}
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                        <input
                          ref={(el) => {
                            if (el) reselectInputsRef.current.set(t.id, el);
                            else reselectInputsRef.current.delete(t.id);
                          }}
                          type="file"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const result = reselectAndResume(t.id, f);
                            if (!result.ok) {
                              setReselectError((m) => ({ ...m, [t.id]: result.error }));
                            } else {
                              setReselectError((m) => ({ ...m, [t.id]: null }));
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<ReselectIcon fontSize="inherit" />}
                          onClick={() => reselectInputsRef.current.get(t.id)?.click()}
                          sx={{ fontSize: '0.7rem', py: 0.4 }}
                        >
                          Re-select file to resume
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => cancel(t.id)}
                          sx={{ fontSize: '0.7rem', py: 0.4 }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
